import { useEffect } from 'react'

import { StructureMetadataDtoRead } from '@/rtkApi/mddApiSliceGeneratedV2'
import { useLazyGetFullTreeV2Query } from '@/rtkApi/mddApiV2Enhanced'
import { logError } from '@/utils/errorHandling'

import { toTreeItem } from '../../constants'
import { StructureTreeItem } from '../../types'
import { TreeDataState } from './types'

interface UseTreeDataLoadingOptions {
	onBatchLoaded?: (nodes: StructureTreeItem[]) => void
}

interface UseTreeDataLoadingReturn {
	isTreeLoading: boolean
}

function processFullTreeResult(result: StructureMetadataDtoRead[]) {
	if (result.length === 0) {
		return { systemItems: [], grouped: {}, allNodes: [] }
	}

	const grouped: Record<string, StructureTreeItem[]> = {}
	const systemItems: StructureTreeItem[] = []
	const allNodes: StructureTreeItem[] = []
	const nodeIdsWithChildren = new Set<string>()

	result.forEach((item) => {
		const treeItem = toTreeItem(item)
		if (item.type === 'SYSTEM') {
			systemItems.push(treeItem)
		} else {
			const parentId = String(item.parent?.id)
			nodeIdsWithChildren.add(parentId)
			if (!grouped[parentId]) grouped[parentId] = []
			grouped[parentId].push(treeItem)
			allNodes.push(treeItem)
		}
	})

	Object.values(grouped).forEach((children) => {
		children.forEach((child) => {
			child.hasChildren = nodeIdsWithChildren.has(child.id)
		})
	})
	systemItems.forEach((system) => {
		system.hasChildren = nodeIdsWithChildren.has(system.id)
	})

	return { systemItems, grouped, allNodes }
}

export const useTreeDataLoading = (
	state: TreeDataState,
	options: UseTreeDataLoadingOptions,
): UseTreeDataLoadingReturn => {
	const {
		setRawSystems,
		setRawChildrenMap,
		setPendingRefreshParentIds,
		setUsedIds,
		setIsFullTreeLoading,
		setInitialLoadDone,
	} = state

	const { onBatchLoaded } = options

	const [fetchTree, { data: fullTreeData, isLoading: isTreeLoading }] =
		useLazyGetFullTreeV2Query()

	// Defer tree fetch until after first paint so bootstrap and content area paint first
	useEffect(() => {
		const id = window.requestIdleCallback
			? window.requestIdleCallback(() => {
					fetchTree({})
				}, { timeout: 300 })
			: setTimeout(() => fetchTree({}), 100)
		return () =>
			window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id)
	}, [fetchTree])

	useEffect(() => {
		if (!fullTreeData) return

		try {
			if (fullTreeData.length === 0) {
				setRawSystems([])
				setRawChildrenMap({})
				setPendingRefreshParentIds(new Set())
				setIsFullTreeLoading(false)
				setInitialLoadDone(true)
				return
			}

			const { systemItems, grouped, allNodes } = processFullTreeResult(fullTreeData)

			setRawSystems(systemItems)
			setRawChildrenMap(grouped)
			setUsedIds((prev) => new Set([
				...prev,
				...systemItems.map((s) => s.id),
				...Object.values(grouped).flat().map((c) => c.id),
			]))
			setPendingRefreshParentIds(new Set())
			setIsFullTreeLoading(false)
			setInitialLoadDone(true)
			onBatchLoaded?.(allNodes)
		} catch (error) {
			logError(error, { operation: 'useTreeDataLoading.processFullTree' })
			setPendingRefreshParentIds(new Set())
			setIsFullTreeLoading(false)
			setInitialLoadDone(true)
		}
	}, [
		fullTreeData,
		onBatchLoaded,
		setRawSystems,
		setRawChildrenMap,
		setUsedIds,
		setPendingRefreshParentIds,
		setIsFullTreeLoading,
		setInitialLoadDone,
	])

	return {
		isTreeLoading,
	}
}

